/**
 * Activity Log API Integration
 * Handles activity feed and audit log endpoints
 */

import axiosClient from '../axiosClient';
import {
  ActivityLog,
  ActivityFeedResponse,
  ActivityFeedParams,
  EntityType,
} from '../types/ActivityTypes';

/**
 * Activity API endpoints
 */
export const activityApi = {
  /**
   * Get workspace activity feed
   */
  getWorkspaceActivityFeed: async (
    workspaceId: string,
    params: ActivityFeedParams = {},
  ): Promise<ActivityFeedResponse> => {
    try {
      const { limit = 50, offset = 0 } = params;
      const response = await axiosClient.get(
        `/activity/workspace/${workspaceId}`,
        {
          params: { limit, offset },
        },
      );
      console.log('✅ Workspace activity feed fetched:', workspaceId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching workspace activity feed:', error);
      throw error;
    }
  },

  /**
   * Get board activity feed
   */
  getBoardActivityFeed: async (
    boardId: string,
    params: ActivityFeedParams = {},
  ): Promise<ActivityFeedResponse> => {
    try {
      const { limit = 50, offset = 0 } = params;
      const response = await axiosClient.get(`/activity/board/${boardId}`, {
        params: { limit, offset },
      });
      console.log('✅ Board activity feed fetched:', boardId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching board activity feed:', error);
      throw error;
    }
  },

  /**
   * Get user's activity feed
   */
  getUserActivityFeed: async (
    params: ActivityFeedParams = {},
  ): Promise<ActivityFeedResponse> => {
    try {
      const { limit = 50, offset = 0 } = params;
      const response = await axiosClient.get('/activity/user', {
        params: { limit, offset },
      });
      console.log('✅ User activity feed fetched');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching user activity feed:', error);
      throw error;
    }
  },

  /**
   * Get recent activity (last N hours)
   */
  getRecentActivity: async (
    workspaceId: string,
    hours: number = 24,
  ): Promise<ActivityLog[]> => {
    try {
      const response = await axiosClient.get(
        `/activity/workspace/${workspaceId}/recent`,
        {
          params: { hours },
        },
      );
      console.log('✅ Recent activity fetched:', workspaceId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching recent activity:', error);
      throw error;
    }
  },

  /**
   * Get activity for a specific entity
   */
  getEntityActivity: async (
    entityType: EntityType,
    entityId: string,
    limit: number = 20,
  ): Promise<ActivityLog[]> => {
    try {
      const response = await axiosClient.get(
        `/activity/entity/${entityType}/${entityId}`,
        {
          params: { limit },
        },
      );
      console.log('✅ Entity activity fetched:', entityType, entityId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching entity activity:', error);
      throw error;
    }
  },
};

export default activityApi;
